<template>
  <div class="job-listings-section">
    <!-- Background Elements -->
    <div class="section-background">
      <div class="bg-pattern"></div>
      <div class="floating-elements">
        <div class="floating-element element-1"></div>
        <div class="floating-element element-2"></div>
        <div class="floating-element element-3"></div>
      </div>
    </div>

    <div class="container mx-auto px-6 py-16">
      <!-- Section Header -->
      <div class="section-header text-center mb-16">
        <div class="header-accent"></div>
        <h2 class="section-title">
          Current <span class="highlight">Opportunities</span> 
        </h2>
        <p class="section-subtitle">
          Explore Open Positions at Stainless Tank & Equipment
        </p>
        <div class="title-divider"></div>
      </div>

      <!-- Job Listings Grid -->
      <div class="jobs-grid">
        <div 
          v-for="job in jobOpenings" 
          :key="job.id"
          class="job-card"
          @click="navigateToJobDetail(job.id)"
        >
          <div class="job-card-header">
            <div class="job-department">
              <font-awesome-icon :icon="job.departmentIcon" class="department-icon" />
              <span>{{ job.department }}</span>
            </div>
            <div class="job-status">
              <span class="status-badge">{{ job.status }}</span>
            </div>
          </div>
          
          <div class="job-card-content">
            <h3 class="job-title">{{ job.title }}</h3>
            <div class="job-meta">
              <div class="meta-item">
                <font-awesome-icon :icon="['fas', 'map-marker-alt']" class="meta-icon" />
                <span class="meta-label">Location:</span>
                <span class="meta-value">{{ job.location }}</span>
              </div>
              <div class="meta-item">
                <font-awesome-icon :icon="['fas', 'id-card']" class="meta-icon" />
                <span class="meta-label">Job #:</span>
                <span class="meta-value">{{ job.jobNumber }}</span>
              </div>
              <div class="meta-item">
                <font-awesome-icon :icon="['fas', 'calendar']" class="meta-icon" />
                <span class="meta-label">Date:</span>
                <span class="meta-value">{{ job.datePosted }}</span>
              </div>
            </div>
            
            <p class="job-description">{{ job.shortDescription }}</p>
            
            <div class="job-highlights">
              <div class="highlight-item" v-for="highlight in job.highlights" :key="highlight">
                <font-awesome-icon :icon="['fas', 'check-circle']" class="highlight-icon" />
                <span>{{ highlight }}</span>
              </div>
            </div>
          </div>
          
          <div class="job-card-footer">
            <div class="job-requirements">
              <span class="requirement-label">Requirements:</span>
              <span class="requirement-text">{{ job.keyRequirement }}</span>
            </div>
            <button class="view-details-btn">
              <span>View Details</span>
              <font-awesome-icon :icon="['fas', 'arrow-right']" class="btn-icon" />
            </button>
          </div>
        </div>
        
        <!-- No Jobs Available State -->
        <div v-if="jobOpenings.length === 0" class="no-jobs-state">
          <font-awesome-icon :icon="['fas', 'search']" class="no-jobs-icon" />
          <h3>No Current Openings</h3>
          <p>We're always looking for talented individuals. Check back soon or submit your application for future opportunities.</p>
          <button @click="scrollToApplication" class="submit-application-btn">
            Submit Application
          </button>
        </div>
      </div>
      
      <!-- General Application CTA -->
      <div class="general-application-cta">
        <div class="cta-content">
          <h3>Don't see the perfect role?</h3>
          <p>We're always interested in meeting talented individuals who share our commitment to manufacturing excellence.</p>
          <button @click="scrollToApplication" class="general-apply-btn">
            <font-awesome-icon :icon="['fas', 'file-upload']" class="btn-icon" />
            Submit General Application
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'JobListings',
  data() {
    return {
      jobOpenings: [
        {
          id: 'JD00078',
          title: 'Senior Recruiter',
          department: 'Human Resources',
          departmentIcon: ['fas', 'users'],
          location: 'On Site',
          jobNumber: 'JD00078',
          datePosted: 'January 14, 2025',
          status: 'Open',
          shortDescription: 'Lead talent acquisition efforts across various departments through innovative recruiting strategies, leveraging advanced HR technologies, and building strong relationships with internal stakeholders.',
          keyRequirement: 'Bachelor\'s degree in Human Resources or related field; proven experience in technical and executive recruiting',
          highlights: [
            'Leadership Role in Talent Acquisition',
            'Strategic HR Impact',
            'Career Growth Opportunities',
            'Comprehensive Benefits'
          ]
        },
        {
          id: 'JD00079',
          title: 'Manufacturing Training Specialist',
          department: 'Human Resources',
          departmentIcon: ['fas', 'chalkboard-teacher'],
          location: 'On Site',
          jobNumber: 'JD00079',
          datePosted: 'January 14, 2025',
          status: 'Open',
          shortDescription: 'Design, develop, and deliver effective training programs on production processes, safety, and equipment for new and existing employees, focusing on hands-on skill-building and standard operating procedures.',
          keyRequirement: 'Bachelor\'s degree in Education, Instructional Design, or related field; Bi-Lingual (Spanish) required',
          highlights: [
            'Impactful Training Role',
            'Cross-Functional Collaboration',
            'Career Growth Opportunities',
            'Comprehensive Benefits'
          ]
        }
      ]
    };
  },
  methods: {
    navigateToJobDetail(jobId) {
      this.$router.push(`/careers/job/${jobId}`);
    },
    
    scrollToApplication() {
      // Scroll to the application form section
      const container = document.querySelector('.overflow-y-auto');
      const element = document.getElementById('slide2');
      if (element && container) {
        container.scrollTo({ top: element.offsetTop, behavior: 'smooth' });
      }
    }
  }
};
</script>

<style scoped>
/* Background and base styling consistent with ApplyNow component */
.job-listings-section {
  position: relative;
  min-height: 100vh;
  padding: 0;
  overflow: hidden;
}

.section-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 50%, #dee2e6 100%);
  z-index: 0;
}

.bg-pattern {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23a22a21' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 0.4;
}

.floating-elements {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 1;
}

.floating-element {
  position: absolute;
  background: linear-gradient(135deg, rgba(162, 42, 33, 0.1), rgba(183, 185, 186, 0.08));
  border-radius: 50%;
  filter: blur(1px);
}

.element-1 {
  width: 300px;
  height: 300px;
  top: 10%;
  left: 5%;
  animation: float 20s ease-in-out infinite;
}

.element-2 {
  width: 200px;
  height: 200px;
  top: 60%;
  right: 10%;
  animation: float 25s ease-in-out infinite reverse;
}

.element-3 {
  width: 150px;
  height: 150px;
  top: 30%;
  left: 60%;
  animation: float 30s ease-in-out infinite;
}

/* Section Header */
.section-header {
  position: relative;
  z-index: 10;
}

.header-accent {
  width: 60px;
  height: 4px;
  background: linear-gradient(135deg, #a22a21, #8f0000);
  margin: 0 auto 2rem;
  border-radius: 2px;
}

.section-title {
  font-size: 3.5rem;
  font-weight: 800;
  color: #2d3748;
  margin-bottom: 1rem;
  text-align: center;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.highlight {
  background: linear-gradient(135deg, #a22a21, #8f0000);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.section-subtitle {
  font-size: 1.5rem;
  color: #4a5568;
  margin-bottom: 3rem;
  font-weight: 500;
}

.title-divider {
  width: 120px;
  height: 2px;
  background: linear-gradient(135deg, #a22a21, transparent);
  margin: 0 auto;
}

/* Jobs Grid */
.jobs-grid {
  position: relative;
  z-index: 10;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
}

/* Job Card */
.job-card {
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(162, 42, 33, 0.1);
  border-radius: 20px;
  padding: 2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
}

.job-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #a22a21, #8f0000);
  transform: scaleX(0);
  transition: transform 0.3s ease;
}

.job-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 16px 48px rgba(162, 42, 33, 0.15);
  border-color: rgba(162, 42, 33, 0.2);
}

.job-card:hover::before {
  transform: scaleX(1);
}

/* Job Card Header */
.job-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.job-department {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #a22a21;
  font-weight: 600;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.department-icon {
  font-size: 1rem;
}

.job-status {
  display: flex;
  align-items: center;
}

.status-badge {
  background: linear-gradient(135deg, #22c55e, #16a34a);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Job Card Content */
.job-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 1rem;
  line-height: 1.2;
}

.job-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #4a5568;
  font-size: 0.9rem;
}

.meta-icon {
  color: #a22a21;
  font-size: 0.9rem;
}

.meta-label {
  color: #a22a21;
  font-weight: 600;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.meta-value {
  color: #4a5568;
}

.job-description {
  color: #4a5568;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  font-size: 1rem;
}

.job-highlights {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.highlight-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #4a5568;
}

.highlight-icon {
  color: #22c55e;
  font-size: 0.8rem;
}

/* Job Card Footer */
.job-card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(162, 42, 33, 0.1);
}

.job-requirements {
  flex: 1;
  text-align: left;
}

.requirement-label {
  font-weight: 600;
  color: #a22a21;
  font-size: 0.85rem;
  margin-right: 0.5rem;
}

.requirement-text {
  color: #4a5568;
  font-size: 0.9rem;
  line-height: 1.4;
}

.view-details-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #a22a21, #8f0000);
  color: white;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
}

.view-details-btn:hover {
  background: linear-gradient(135deg, #8f0000, #7d0000);
  transform: translateX(2px);
}

.btn-icon {
  transition: transform 0.3s ease;
}

.view-details-btn:hover .btn-icon {
  transform: translateX(3px);
}

/* No Jobs State */
.no-jobs-state {
  grid-column: 1 / -1;
  text-align: center;
  padding: 4rem 2rem;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 20px;
  backdrop-filter: blur(10px);
  border: 2px dashed rgba(162, 42, 33, 0.2);
}

.no-jobs-icon {
  font-size: 4rem;
  color: #a22a21;
  margin-bottom: 1.5rem;
  opacity: 0.7;
}

.no-jobs-state h3 {
  font-size: 1.75rem;
  color: #2d3748;
  margin-bottom: 1rem;
  font-weight: 700;
}

.no-jobs-state p {
  color: #4a5568;
  margin-bottom: 2rem;
  font-size: 1.1rem;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
}

.submit-application-btn {
  background: linear-gradient(135deg, #a22a21, #8f0000);
  color: white;
  padding: 1rem 2rem;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1rem;
}

.submit-application-btn:hover {
  background: linear-gradient(135deg, #8f0000, #7d0000);
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(162, 42, 33, 0.3);
}

/* General Application CTA */
.general-application-cta {
  position: relative;
  z-index: 10;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(162, 42, 33, 0.1);
  border-radius: 20px;
  padding: 3rem;
  text-align: center;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.cta-content h3 {
  font-size: 2rem;
  color: #2d3748;
  margin-bottom: 1rem;
  font-weight: 700;
}

.cta-content p {
  color: #4a5568;
  margin-bottom: 2rem;
  font-size: 1.1rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.general-apply-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background: linear-gradient(135deg, #a22a21, #8f0000);
  color: white;
  padding: 1rem 2rem;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1rem;
}

.general-apply-btn:hover {
  background: linear-gradient(135deg, #8f0000, #7d0000);
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(162, 42, 33, 0.3);
}

/* Animations */
@keyframes float {
  0%, 100% { 
    transform: translateY(0) rotate(0deg); 
  }
  50% { 
    transform: translateY(-20px) rotate(2deg); 
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .jobs-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
  
  .job-card {
    padding: 1.5rem;
  }
  
  .job-card-footer {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
  
  .view-details-btn {
    justify-content: center;
  }
  
  .section-title {
    font-size: 2.5rem;
  }
  
  .job-meta {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .job-highlights {
    grid-template-columns: 1fr;
  }
  
  .general-application-cta {
    padding: 2rem;
  }
  
  .cta-content h3 {
    font-size: 1.5rem;
  }
}

@media (max-width: 480px) {
  .job-card {
    padding: 1rem;
  }
  
  .section-title {
    font-size: 2rem;
  }
  
  .job-title {
    font-size: 1.5rem;
  }
  
  .general-application-cta {
    padding: 1.5rem;
  }
}
</style>